<script lang="ts">
  import { Button } from "$lib/ui/button";
  import { DrawerFooter, DrawerHeader, DrawerTitle } from "$lib/ui/drawer";
  import { LoadingSpinner } from "$lib/ui/loading-spinner";
  import { environment } from "$lib/utils/env";
  import { copyToClipboard } from "$lib/utils/helpers";
  import { ArrowUpRight, CopyIcon, LoaderCircle } from "lucide-svelte";

  const {
    onAdminInvite,
    onVerifyDelegation,
    provider,
    allowedDomains = [],
  }: {
    onAdminInvite: () => void;
    onVerifyDelegation: (provider: string, scopes: string[], adminEmail: string) => Promise<void>;
    provider: {
      name: string;
      delegatedPermissions: { description: string; scopes: string[] }[];
      applicationPermissions: { description: string; scopes: string[] }[];
      id: string;
    };
    allowedDomains?: string[];
  } = $props();

  const providerLinks: { [key: string]: { link: string; label: string } } = {
    gdrive: { link: "https://admin.google.com/ac/owl/domainwidedelegation", label: "Google Admin Console" },
  };

  let adminEmail = $state<string>("");
  let isPending = $state<boolean>(false);

  // Validate email domain against allowed domains
  let emailDomainError = $derived.by(() => {
    if (!adminEmail || allowedDomains.length === 0) return null;
    const emailDomain = adminEmail.split("@")[1];
    if (!emailDomain) return null;
    if (!allowedDomains.includes(emailDomain)) {
      return `Email domain must be one of: ${allowedDomains.join(", ")}`;
    }
    return null;
  });

  let isEmailValid = $derived(!emailDomainError && adminEmail.length > 0 && adminEmail.includes("@"));
</script>

<DrawerHeader class="pt-8 px-6 pb-2">
  <DrawerTitle class="text-3xl text-left">Set Up Admin Delegation for {provider.name}</DrawerTitle>
</DrawerHeader>
<div class="my-2 gap-6 px-6 overflow-y-auto grow">
  <div class="grow">
    <div class="flex flex-col gap-3">
      <p class="font-semibold text-offblack">
        Permissions Assurance uses admin domain wide delegation to access your {provider.name} data.
      </p>
      <p class="font-semibold text-offblack">
        We need to set up a trust relationship between our service account and your workspace in order to connect to {provider.name}
        on behalf of your users.
      </p>
      <div class="bg-orange-400 p-2 my-2 mx-3 text-white font-bold rounded-md flex items-center">
        <div class="mx-3 text-3xl">🚨</div>
        <span class="">
          Domain wide delegation allows Permissions Assurance to impersonate all users in the domain according to the allowed
          scopes. Please ensure you understand what scopes you are authorizing.</span
        >
      </div>
      <ol class="list-decimal list-outside space-y-2 mx-6">
        <li>
          Navigate to <a
            target="_blank"
            rel="noopener noreferrer"
            class="font-bold underline"
            href={providerLinks[provider.id].link}>{providerLinks[provider.id].label}</a
          > and add a new API client.
        </li>

        {#await environment}
          <p>Loading environment...</p>
        {:then env}
          <li class="">
            The <span class="font-bold">Client ID</span> is
            <button
              class="inline-flex gap-1 items-center bg-slate-100 px-2 rounded-md py-1 mx-1"
              onclick={() => copyToClipboard(env.googleOAuth2ClientId)}
              ><code class="font-mono">{env.googleOAuth2ClientId}</code><CopyIcon
                class="inline-block"
                size="14"
              /></button
            >
            paste this into the <a href={providerLinks[provider.id].link}>{providerLinks[provider.id].label}</a> client ID
            field to set up domain wide delegation for Permissions Assurance.
          </li>
        {:catch error}
          <p>Error loading environment: {error.message}</p>
        {/await}
        <li>Copy and Paste the following scopes in the new client form:</li>
        {@render scope_list()}
        <li>
          After you have authorized domain wide delegation, return to this page and enter the email address of an admin
          account that will be used to initiate the scan and capture of data.

          <form class="flex flex-col gap-3 mt-2 mb-10">
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <input
                  type="email"
                  placeholder="admin@company.com"
                  class="border rounded-md px-2 py-1 w-full {emailDomainError ? 'border-red-500' : 'border-gray-300'}"
                  bind:value={adminEmail}
                  required
                />
              </div>
              {#if emailDomainError}
                <p class="text-red-500 text-sm">{emailDomainError}</p>
              {/if}
            </div>
          </form>
        </li>
      </ol>
    </div>
  </div>
</div>
<DrawerFooter class="px-6">
  <Button
    disabled={isPending || !isEmailValid}
    on:click={async () => {
      isPending = true;
      await onVerifyDelegation(
        provider.id,
        [...provider.delegatedPermissions]
          .map((x) => x.scopes)
          .reduce((scopes, newScopes) => [...scopes, ...newScopes], []),
        adminEmail,
      );
      isPending = false;
    }}
  >
    <div class="flex items-center gap-2">
      {#if isPending}
        <LoaderCircle class="animate-spin" size="18" />
      {/if}
      <span>Verify {provider.name} delegation </span>
    </div>
  </Button>
  <Button on:click={onAdminInvite} disabled={isPending}>
    Not a {provider.name} admin? Request access
  </Button>
</DrawerFooter>

{#snippet scope_list()}
  <div class="flex flex-col gap-3 mt-5">
    <div class="border flex flex-col gap-2 divide-y-2">
      <div class="px-6 py-4">
        <h2 class="font-semibold text-xl">Delegated Permissions</h2>
        <p>These are used on behalf of users</p>
      </div>
      <div class="divide-y-2 p-6 flex flex-col space-y-3 *:py-3 first:*:pt-0 last:*:pb-0">
        {#each provider.delegatedPermissions as permission}
          <div class="space-y-2">
            <p>
              {permission.description}
            </p>
            <ul class="flex flex-col gap-2">
              {#each permission.scopes as scope}
                <li
                  class="flex items-center justify-between font-mono bg-[#F8FFC8] border border-[#F0FE98] px-2 py-1 rounded-md text-offblack text-xs"
                >
                  <span class="break-all pr-2">{scope}</span>
                  <button class="shrink-0" onclick={() => copyToClipboard(scope)}>
                    <CopyIcon class="inline-block" size="18" />
                  </button>
                </li>
              {/each}
            </ul>
          </div>
        {/each}
      </div>
    </div>
  </div>
{/snippet}
